import React, { useEffect, useState } from "react";
import { Card, CardBody, Container, Row, Col, Button, Modal, ModalHeader, ModalBody, ModalFooter, Input, Label, Alert, Collapse, Spinner as ReactstrapSpinner, Table } from "reactstrap";
import { PIIPatternService } from "../../services/PIIPatternService";
import { PatternGroup, PatternItem, FunctionMappingsResponse } from "../../models/PIIPattern";
import Spinner from "../../common/spinner/spinner";

const PIIPatternPage: React.FC = () => {
  console.log('PIIPatternPage component rendered');
  const [patternGroups, setPatternGroups] = useState<PatternGroup[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [editModal, setEditModal] = useState<boolean>(false);
  const [selectedPattern, setSelectedPattern] = useState<PatternItem | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [editPatterns, setEditPatterns] = useState<string[]>([]);
  const [alert, setAlert] = useState<{ type: 'success' | 'danger', message: string } | null>(null);
  const [updating, setUpdating] = useState<boolean>(false);

  const [addModal, setAddModal] = useState<boolean>(false);
  const [newLabel, setNewLabel] = useState<string>('');
  const [newPatternValue, setNewPatternValue] = useState<string>('');
  const [adding, setAdding] = useState<boolean>(false);

  // Pattern Group Modal states
  const [addPatternGroupModal, setAddPatternGroupModal] = useState<boolean>(false);
  const [newGroupName, setNewGroupName] = useState<string>('');
  const [newGroupDescription, setNewGroupDescription] = useState<string>('');
  const [addingGroup, setAddingGroup] = useState<boolean>(false);

  // Edit Pattern Group Modal states
  const [editPatternGroupModal, setEditPatternGroupModal] = useState<boolean>(false);
  const [editingGroupId, setEditingGroupId] = useState<string>('');
  const [editGroupName, setEditGroupName] = useState<string>('');
  const [editGroupDescription, setEditGroupDescription] = useState<string>('');
  const [updatingGroup, setUpdatingGroup] = useState<boolean>(false);

  // Add Pattern to Group Modal states
  const [addPatternToGroupModal, setAddPatternToGroupModal] = useState<boolean>(false);
  const [targetGroupId, setTargetGroupId] = useState<string>('');
  const [targetGroupName, setTargetGroupName] = useState<string>('');
  const [newPatternLabel, setNewPatternLabel] = useState<string>('');
  const [newPatternRegex, setNewPatternRegex] = useState<string>('');
  const [addingPatternToGroup, setAddingPatternToGroup] = useState<boolean>(false);

  // Function mapping states
  const [functionMappings, setFunctionMappings] = useState<{[key: string]: string}>({
    'PIE Notes Generation': '',
    'Assessment Generation': '',
    'Text Summarization Conversation': '',
    'Notes Generation': ''
  });

  useEffect(() => {
    loadPatterns();
    loadFunctionMappings();
  }, []);

  // Load function mappings again after pattern groups are loaded
  useEffect(() => {
    if (patternGroups.length > 0) {
      console.log('Pattern groups loaded, reloading function mappings...');
      loadFunctionMappings();
    }
  }, [patternGroups]);

  const loadPatterns = async () => {
    console.log('loadPatterns called');
    setLoading(true);
    try {
      console.log('Calling getAllPatternGroups...');
      const response = await PIIPatternService.getAllPatternGroups();
      console.log('Full API response:', response);
      console.log('Response success:', response.success);
      console.log('Response data:', response.data);

      if (response.success && response.data) {
        console.log('Groups from response:', response.data.groups);
        console.log('Groups length:', response.data.groups?.length);
        setPatternGroups(response.data.groups || []);
        console.log('Pattern groups set successfully');
      } else {
        console.log('API error - success:', response.success);
        console.log('API error - message:', response.message);
        showAlert('danger', response.message || 'Failed to load pattern groups');
      }
    } catch (error) {
      console.log('Exception caught:', error);
      console.error('Full error:', error);
      showAlert('danger', 'Error loading pattern groups');
    } finally {
      console.log('Setting loading to false');
      setLoading(false);
    }
  };

  const loadFunctionMappings = async () => {
    console.log('loadFunctionMappings called');
    try {
      console.log('Calling getFunctionGroupMappings...');
      const response = await PIIPatternService.getFunctionGroupMappings();
      console.log('Function mappings API response:', response);

      if (response.success && response.data) {
        const mappingsData: FunctionMappingsResponse = response.data;
        console.log('Mappings data:', mappingsData);
        console.log('Mappings array:', mappingsData.mappings);

        // Mapping from API function names to display names
        const apiToDisplayMapping: {[key: string]: string} = {
          'PIE_NOTES_GENERATION': 'PIE Notes Generation',
          'ASSESSMENT_GENERATION': 'Assessment Generation',
          'TEXT_SUMMARIZATION_CONVERSATION': 'Text Summarization Conversation',
          'NOTES_GENERATION': 'Notes Generation'
        };

        // Create new mappings object with current values
        const newMappings = { ...functionMappings };
        console.log('Initial newMappings:', newMappings);

        mappingsData.mappings.forEach(mapping => {
          console.log('Processing mapping:', mapping);
          const displayName = apiToDisplayMapping[mapping.function_name];
          console.log(`Function name: ${mapping.function_name} -> Display name: ${displayName}`);
          if (displayName) {
            newMappings[displayName] = mapping.group_pattern_id;
            console.log(`Set ${displayName} = ${mapping.group_pattern_id}`);
          }
        });

        console.log('Final newMappings:', newMappings);
        setFunctionMappings(newMappings);
        console.log('Function mappings updated successfully');
      } else {
        console.error('Failed to load function mappings:', response.message);
      }
    } catch (error) {
      console.error('Error loading function mappings:', error);
    }
  };

  const showAlert = (type: 'success' | 'danger', message: string) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000);
  };

  const handleSaveMappings = async () => {
    setUpdating(true);

    // Mapping from display names to API function names
    const functionNameMapping: {[key: string]: string} = {
      'PIE Notes Generation': 'PIE_NOTES_GENERATION',
      'Assessment Generation': 'ASSESSMENT_GENERATION',
      'Text Summarization Conversation': 'TEXT_SUMMARIZATION_CONVERSATION',
      'Notes Generation': 'NOTES_GENERATION'
    };

    try {
      const promises = Object.keys(functionMappings).map(async (displayName) => {
        const groupPatternId = functionMappings[displayName];
        const functionName = functionNameMapping[displayName];

        if (groupPatternId && functionName) {
          return PIIPatternService.updateFunctionGroupMapping(functionName, groupPatternId);
        }
        return null;
      });

      const results = await Promise.all(promises.filter(p => p !== null));

      // Check if all requests were successful
      const allSuccessful = results.every(result => result?.success);

      if (allSuccessful) {
        showAlert('success', 'Function mappings saved successfully');
        // Reload mappings to get updated data
        loadFunctionMappings();
      } else {
        const failedResults = results.filter(result => !result?.success);
        console.error('Some mappings failed:', failedResults);
        showAlert('danger', 'Some function mappings failed to save');
      }
    } catch (error) {
      console.error('Error saving mappings:', error);
      showAlert('danger', 'Failed to save function mappings');
    } finally {
      setUpdating(false);
    }
  };

  const toggleGroup = (groupId: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  const handleEdit = (groupId: string, pattern: PatternItem) => {
    setSelectedGroupId(groupId);
    setSelectedPattern(pattern);
    setEditPatterns([...pattern.patterns]);
    setEditModal(true);
  };

  const handleSave = async () => {
    if (!selectedPattern || !selectedGroupId) return;

    setUpdating(true);
    try {
      // Use group-specific update API
      const response = await PIIPatternService.updatePatternsInGroup(
        selectedGroupId,
        selectedPattern.label,
        editPatterns.filter(pattern => pattern.trim() !== '') // Remove empty patterns
      );

      if (response.success) {
        showAlert('success', 'Pattern updated successfully');
        setEditModal(false);
        loadPatterns(); // Reload to get updated data
      } else {
        showAlert('danger', response.message || 'Failed to update pattern');
      }
    } catch (error) {
      showAlert('danger', 'Error updating pattern');
    } finally {
      setUpdating(false);
    }
  };

  const addNewPattern = () => {
    setEditPatterns([...editPatterns, ""]);
  };

  const removePattern = (index: number) => {
    const newPatterns = editPatterns.filter((_, i) => i !== index);
    setEditPatterns(newPatterns);
  };

  const updatePattern = (index: number, value: string) => {
    const newPatterns = [...editPatterns];
    newPatterns[index] = value;
    setEditPatterns(newPatterns);
  };



  const handleAddPattern = async () => {
    if (!newLabel.trim() || !newPatternValue.trim()) {
      showAlert('danger', 'Please fill in both label and pattern fields');
      return;
    }

    setAdding(true);
    try {
      const response = await PIIPatternService.addPattern(newLabel.trim(), newPatternValue.trim());
      if (response.success) {
        showAlert('success', `Pattern "${newLabel}" added successfully`);
        setAddModal(false);
        setNewLabel('');
        setNewPatternValue('');
        loadPatterns(); // Reload patterns after add
      } else {
        showAlert('danger', response.message || 'Failed to add pattern');
      }
    } catch (error) {
      showAlert('danger', 'Error adding pattern');
    } finally {
      setAdding(false);
    }
  };

  const openAddModal = () => {
    setNewLabel('');
    setNewPatternValue('');
    setAddModal(true);
  };

  const handleAddNewPatternGroup = () => {
    setNewGroupName('');
    setNewGroupDescription('');
    setAddPatternGroupModal(true);
  };

  const closeAddPatternGroupModal = () => {
    setAddPatternGroupModal(false);
    setNewGroupName('');
    setNewGroupDescription('');
  };

  const submitNewPatternGroup = async () => {
    if (!newGroupName.trim()) {
      window.alert('Pattern group name is required');
      return;
    }

    try {
      setAddingGroup(true);
      const response = await PIIPatternService.createPatternGroup(newGroupName.trim(), newGroupDescription.trim());

      if (response.success) {
        console.log('Pattern group created successfully:', response);
        // Reload pattern groups
        loadPatterns();
        closeAddPatternGroupModal();
      } else {
        console.error('Failed to create pattern group:', response.message);
        window.alert('Failed to create pattern group: ' + response.message);
      }
    } catch (error) {
      console.error('Error creating pattern group:', error);
      window.alert('Error creating pattern group');
    } finally {
      setAddingGroup(false);
    }
  };

  const handleDeletePatternGroup = async (groupId: string, groupName: string) => {
    const confirmDelete = window.confirm(`Are you sure you want to delete the pattern group "${groupName}"? This action cannot be undone.`);
    if (!confirmDelete) return;

    try {
      setLoading(true);
      const response = await PIIPatternService.deletePatternGroup(groupId);

      if (response.success) {
        console.log('Pattern group deleted successfully:', response);
        // Reload pattern groups
        loadPatterns();
      } else {
        console.error('Failed to delete pattern group:', response.message);
        window.alert('Failed to delete pattern group: ' + response.message);
      }
    } catch (error) {
      console.error('Error deleting pattern group:', error);
      window.alert('Error deleting pattern group');
    } finally {
      setLoading(false);
    }
  };

  const handleEditPatternGroup = (group: PatternGroup) => {
    setEditingGroupId(group.id);
    setEditGroupName(group.name);
    setEditGroupDescription(group.description || '');
    setEditPatternGroupModal(true);
  };

  const closeEditPatternGroupModal = () => {
    setEditPatternGroupModal(false);
    setEditingGroupId('');
    setEditGroupName('');
    setEditGroupDescription('');
  };

  const submitEditPatternGroup = async () => {
    if (!editGroupName.trim()) {
      window.alert('Pattern group name is required');
      return;
    }

    try {
      setUpdatingGroup(true);
      const response = await PIIPatternService.updatePatternGroup(editingGroupId, editGroupName.trim(), editGroupDescription.trim());

      if (response.success) {
        console.log('Pattern group updated successfully:', response);
        // Reload pattern groups
        loadPatterns();
        closeEditPatternGroupModal();
      } else {
        console.error('Failed to update pattern group:', response.message);
        window.alert('Failed to update pattern group: ' + response.message);
      }
    } catch (error) {
      console.error('Error updating pattern group:', error);
      window.alert('Error updating pattern group');
    } finally {
      setUpdatingGroup(false);
    }
  };

  const handleAddPatternToGroup = (group: PatternGroup) => {
    setTargetGroupId(group.id);
    setTargetGroupName(group.name);
    setNewPatternLabel('');
    setNewPatternRegex('');
    setAddPatternToGroupModal(true);
  };

  const closeAddPatternToGroupModal = () => {
    setAddPatternToGroupModal(false);
    setTargetGroupId('');
    setTargetGroupName('');
    setNewPatternLabel('');
    setNewPatternRegex('');
  };

  const submitAddPatternToGroup = async () => {
    if (!newPatternLabel.trim()) {
      window.alert('Pattern label is required');
      return;
    }
    if (!newPatternRegex.trim()) {
      window.alert('Pattern regex is required');
      return;
    }

    try {
      setAddingPatternToGroup(true);
      const response = await PIIPatternService.createPatternInGroup(targetGroupId, newPatternLabel.trim(), newPatternRegex.trim());

      if (response.success) {
        console.log('Pattern added to group successfully:', response);
        // Reload pattern groups
        loadPatterns();
        closeAddPatternToGroupModal();
      } else {
        console.error('Failed to add pattern to group:', response.message);
        window.alert('Failed to add pattern to group: ' + response.message);
      }
    } catch (error) {
      console.error('Error adding pattern to group:', error);
      window.alert('Error adding pattern to group');
    } finally {
      setAddingPatternToGroup(false);
    }
  };

  const handleDeletePatternFromGroup = async (groupId: string, patternLabel: string, groupName: string) => {
    const confirmDelete = window.confirm(`Are you sure you want to delete the pattern "${patternLabel}" from group "${groupName}"? This action cannot be undone.`);
    if (!confirmDelete) return;

    try {
      setLoading(true);
      const response = await PIIPatternService.deletePatternFromGroup(groupId, patternLabel);

      if (response.success) {
        console.log('Pattern deleted from group successfully:', response);
        // Reload pattern groups
        loadPatterns();
      } else {
        console.error('Failed to delete pattern from group:', response.message);
        window.alert('Failed to delete pattern from group: ' + response.message);
      }
    } catch (error) {
      console.error('Error deleting pattern from group:', error);
      window.alert('Error deleting pattern from group');
    } finally {
      setLoading(false);
    }
  };

  const closeAddModal = () => {
    setAddModal(false);
    setNewLabel('');
    setNewPatternValue('');
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
            <Spinner />
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col xs="12">
            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
              <h4 className="mb-sm-0 font-size-18">PII Pattern Group Management</h4>
            </div>
          </Col>
        </Row>

        {alert && (
          <Row>
            <Col xs="12">
              <Alert color={alert.type} className="alert-dismissible fade show">
                {alert.message}
              </Alert>
            </Col>
          </Row>
        )}

        <Row>
          <Col xs="12">
            <Card>
              <CardBody>
                <h5 className="mb-4">Function Pattern Group Mapping</h5>
                <div className="table-responsive">
                  <Table className="table-bordered">
                    <thead>
                      <tr>
                        <th style={{ width: '40%' }}>Function</th>
                        <th style={{ width: '60%' }}>Pattern Group</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.keys(functionMappings).map((functionName) => (
                        <tr key={functionName}>
                          <td>
                            <strong>{functionName}</strong>
                          </td>
                          <td>
                            <Input
                              type="select"
                              value={functionMappings[functionName]}
                              onChange={(e) => setFunctionMappings({
                                ...functionMappings,
                                [functionName]: e.target.value
                              })}
                            >
                              <option value="">Select Pattern Group</option>
                              {patternGroups.map((group) => (
                                <option key={group.id} value={group.id}>
                                  {group.name} ({group.patterns.length} patterns)
                                </option>
                              ))}
                            </Input>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                <div className="mt-4">
                  <Button
                    color="primary"
                    onClick={handleSaveMappings}
                    className="me-2"
                    disabled={updating}
                  >
                    {updating ? (
                      <>
                        <i className="bx bx-loader-alt bx-spin me-1"></i>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="bx bx-save me-1"></i>
                        Save Mappings
                      </>
                    )}
                  </Button>
                </div>
              </CardBody>
            </Card>

            {/* Pattern Groups Management Section */}
            <Card className="mt-4">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="mb-0">Pattern Groups Management</h5>
                  <Button
                    color="success"
                    onClick={handleAddNewPatternGroup}
                  >
                    <i className="bx bx-plus me-1"></i>
                    Add New Pattern Group
                  </Button>
                </div>

                {/* Collapsible Pattern Groups */}
                <div className="pattern-groups-list">
                  {patternGroups.map((group) => (
                    <Card key={group.id} className="mb-3 border">
                      <CardBody className="p-0">
                        {/* Clickable Header */}
                        <div
                          className="d-flex justify-content-between align-items-center p-3 cursor-pointer border-bottom"
                          onClick={() => toggleGroup(group.id)}
                          style={{
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease',
                            backgroundColor: expandedGroups.has(group.id) ? '#f8f9fa' : 'transparent'
                          }}
                          onMouseEnter={(e) => {
                            if (!expandedGroups.has(group.id)) {
                              e.currentTarget.style.backgroundColor = '#f8f9fa';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!expandedGroups.has(group.id)) {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }
                          }}
                        >
                          <div className="d-flex align-items-center flex-grow-1">
                            <div className="me-3">
                              <i className={`bx ${expandedGroups.has(group.id) ? 'bx-chevron-down' : 'bx-chevron-right'} fs-5`}></i>
                            </div>
                            <div className="flex-grow-1">
                              <h6 className="mb-1 fw-bold">{group.name}</h6>
                              <p className="mb-0 text-muted small">
                                {group.description || 'No description'} • {group.patterns.length} patterns • Created {new Date(group.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            <div className="me-3">
                              <span className="badge badge-soft-primary">
                                {group.patterns.length} patterns
                              </span>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="d-flex gap-1" onClick={(e) => e.stopPropagation()}>
                            <Button
                              color="warning"
                              size="sm"
                              onClick={() => handleEditPatternGroup(group)}
                              title="Edit Group"
                            >
                              <i className="bx bx-edit"></i>
                            </Button>
                            <Button
                              color="danger"
                              size="sm"
                              onClick={() => handleDeletePatternGroup(group.id, group.name)}
                              title="Delete Group"
                            >
                              <i className="bx bx-trash"></i>
                            </Button>
                          </div>
                        </div>

                        {/* Collapsible Content */}
                        <Collapse isOpen={expandedGroups.has(group.id)}>
                          <div className="p-3 bg-light">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <h6 className="mb-0">Patterns in "{group.name}"</h6>
                              <Button
                                color="success"
                                size="sm"
                                onClick={() => handleAddPatternToGroup(group)}
                              >
                                <i className="bx bx-plus me-1"></i>
                                Add Pattern
                              </Button>
                            </div>

                            {group.patterns.length > 0 ? (
                              <div className="table-responsive">
                                <Table className="table-bordered table-striped table-sm mb-0">
                                  <thead>
                                    <tr>
                                      <th>Label</th>
                                      <th>Patterns Count</th>
                                      <th>Sample Pattern</th>
                                      <th>Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {group.patterns.map((pattern) => (
                                      <tr key={pattern.label}>
                                        <td>
                                          <strong>{pattern.label}</strong>
                                        </td>
                                        <td>
                                          <span className="badge badge-soft-info">
                                            {pattern.patterns.length} patterns
                                          </span>
                                        </td>
                                        <td>
                                          <code className="text-muted small">
                                            {pattern.patterns[0] || 'No patterns'}
                                          </code>
                                        </td>
                                        <td>
                                          <Button
                                            color="primary"
                                            size="sm"
                                            onClick={() => handleEdit(group.id, pattern)}
                                            className="me-2"
                                          >
                                            <i className="bx bx-edit-alt me-1"></i>
                                            Edit
                                          </Button>
                                          <Button
                                            color="danger"
                                            size="sm"
                                            onClick={() => handleDeletePatternFromGroup(group.id, pattern.label, group.name)}
                                            disabled={loading}
                                          >
                                            {loading ? (
                                              <>
                                                <i className="bx bx-loader-alt bx-spin me-1"></i>
                                                Deleting...
                                              </>
                                            ) : (
                                              <>
                                                <i className="bx bx-trash me-1"></i>
                                                Delete
                                              </>
                                            )}
                                          </Button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              </div>
                            ) : (
                              <div className="text-center py-3">
                                <i className="bx bx-info-circle text-muted fs-4"></i>
                                <p className="text-muted mb-0 mt-2">No patterns in this group yet.</p>
                              </div>
                            )}
                          </div>
                        </Collapse>
                      </CardBody>
                    </Card>
                  ))}
                </div>

                {patternGroups.length === 0 && (
                  <div className="text-center py-5">
                    <i className="bx bx-search-alt-2 display-4 text-muted"></i>
                    <h5 className="mt-3">No Pattern Groups Found</h5>
                    <p className="text-muted">No pattern groups are available at the moment.</p>
                  </div>
                )}


              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Edit Modal */}
        <Modal isOpen={editModal} toggle={() => setEditModal(false)} size="lg">
          <ModalHeader toggle={() => setEditModal(false)}>
            Edit Patterns for {selectedPattern?.label}
          </ModalHeader>
          <ModalBody>
            <div className="mb-3">
              <Label>Patterns:</Label>
              {editPatterns.map((pattern, index) => (
                <div key={index} className="d-flex mb-2">
                  <Input
                    type="text"
                    value={pattern}
                    onChange={(e) => updatePattern(index, e.target.value)}
                    placeholder="Enter regex pattern"
                    className="me-2"
                  />
                  <Button
                    color="danger"
                    size="sm"
                    onClick={() => removePattern(index)}
                    disabled={editPatterns.length === 1}
                  >
                    <i className="bx bx-trash"></i>
                  </Button>
                </div>
              ))}
              <Button
                color="success"
                size="sm"
                onClick={addNewPattern}
                className="mt-2"
              >
                <i className="bx bx-plus me-1"></i>
                Add New Pattern
              </Button>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setEditModal(false)}>
              Cancel
            </Button>
            <Button 
              color="primary" 
              onClick={handleSave}
              disabled={updating}
            >
              {updating ? (
                <>
                  <i className="bx bx-loader bx-spin me-1"></i>
                  Updating...
                </>
              ) : (
                <>
                  <i className="bx bx-save me-1"></i>
                  Save Changes
                </>
              )}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Add Pattern Modal */}
        <Modal isOpen={addModal} toggle={closeAddModal} size="lg">
          <ModalHeader toggle={closeAddModal}>
            Add New Pattern
          </ModalHeader>
          <ModalBody>
            <div className="mb-3">
              <Label for="newLabel">Label *</Label>
              <Input
                type="text"
                id="newLabel"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="Enter pattern label (e.g., EMAIL, PHONE, SSN)"
              />
            </div>
            <div className="mb-3">
              <Label for="newPattern">Pattern *</Label>
              <Input
                type="textarea"
                id="newPattern"
                rows={4}
                value={newPatternValue}
                onChange={(e) => setNewPatternValue(e.target.value)}
                placeholder="Enter regex pattern (e.g., [a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})"
              />
              <small className="text-muted">
                Enter a regular expression pattern to match the PII data type.
              </small>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={closeAddModal}>
              Cancel
            </Button>
            <Button
              color="success"
              onClick={handleAddPattern}
              disabled={adding || !newLabel.trim() || !newPatternValue.trim()}
            >
              {adding ? (
                <>
                  <i className="bx bx-loader-alt bx-spin me-1"></i>
                  Adding...
                </>
              ) : (
                <>
                  <i className="bx bx-plus me-1"></i>
                  Add Pattern
                </>
              )}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Add Pattern Group Modal */}
        <Modal isOpen={addPatternGroupModal} toggle={closeAddPatternGroupModal} size="md">
          <ModalHeader toggle={closeAddPatternGroupModal}>
            Add New Pattern Group
          </ModalHeader>
          <ModalBody>
            <div className="mb-3">
              <Label for="groupName">Group Name *</Label>
              <Input
                type="text"
                id="groupName"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                placeholder="Enter pattern group name"
                disabled={addingGroup}
              />
            </div>
            <div className="mb-3">
              <Label for="groupDescription">Description</Label>
              <Input
                type="textarea"
                id="groupDescription"
                value={newGroupDescription}
                onChange={(e) => setNewGroupDescription(e.target.value)}
                placeholder="Enter pattern group description (optional)"
                rows={3}
                disabled={addingGroup}
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={closeAddPatternGroupModal} disabled={addingGroup}>
              Cancel
            </Button>
            <Button color="success" onClick={submitNewPatternGroup} disabled={addingGroup || !newGroupName.trim()}>
              {addingGroup ? (
                <>
                  <ReactstrapSpinner size="sm" className="me-1" />
                  Creating...
                </>
              ) : (
                <>
                  <i className="bx bx-plus me-1"></i>
                  Create Group
                </>
              )}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Edit Pattern Group Modal */}
        <Modal isOpen={editPatternGroupModal} toggle={closeEditPatternGroupModal} size="md">
          <ModalHeader toggle={closeEditPatternGroupModal}>
            Edit Pattern Group
          </ModalHeader>
          <ModalBody>
            <div className="mb-3">
              <Label for="editGroupName">Group Name *</Label>
              <Input
                type="text"
                id="editGroupName"
                value={editGroupName}
                onChange={(e) => setEditGroupName(e.target.value)}
                placeholder="Enter pattern group name"
                disabled={updatingGroup}
              />
            </div>
            <div className="mb-3">
              <Label for="editGroupDescription">Description</Label>
              <Input
                type="textarea"
                id="editGroupDescription"
                value={editGroupDescription}
                onChange={(e) => setEditGroupDescription(e.target.value)}
                placeholder="Enter pattern group description (optional)"
                rows={3}
                disabled={updatingGroup}
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={closeEditPatternGroupModal} disabled={updatingGroup}>
              Cancel
            </Button>
            <Button color="warning" onClick={submitEditPatternGroup} disabled={updatingGroup || !editGroupName.trim()}>
              {updatingGroup ? (
                <>
                  <ReactstrapSpinner size="sm" className="me-1" />
                  Updating...
                </>
              ) : (
                <>
                  <i className="bx bx-save me-1"></i>
                  Update Group
                </>
              )}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Add Pattern to Group Modal */}
        <Modal isOpen={addPatternToGroupModal} toggle={closeAddPatternToGroupModal} size="lg">
          <ModalHeader toggle={closeAddPatternToGroupModal}>
            Add Pattern to "{targetGroupName}"
          </ModalHeader>
          <ModalBody>
            <div className="mb-3">
              <Label for="patternLabel">Pattern Label *</Label>
              <Input
                type="text"
                id="patternLabel"
                value={newPatternLabel}
                onChange={(e) => setNewPatternLabel(e.target.value)}
                placeholder="Enter pattern label (e.g., EMAIL, PHONE, SSN)"
                disabled={addingPatternToGroup}
              />
            </div>
            <div className="mb-3">
              <Label for="patternRegex">Pattern Regex *</Label>
              <Input
                type="textarea"
                id="patternRegex"
                value={newPatternRegex}
                onChange={(e) => setNewPatternRegex(e.target.value)}
                placeholder="Enter regex pattern (e.g., [a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})"
                rows={4}
                disabled={addingPatternToGroup}
              />
              <small className="text-muted">
                Enter a valid regex pattern to match the data you want to detect.
              </small>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={closeAddPatternToGroupModal} disabled={addingPatternToGroup}>
              Cancel
            </Button>
            <Button color="success" onClick={submitAddPatternToGroup} disabled={addingPatternToGroup || !newPatternLabel.trim() || !newPatternRegex.trim()}>
              {addingPatternToGroup ? (
                <>
                  <ReactstrapSpinner size="sm" className="me-1" />
                  Adding...
                </>
              ) : (
                <>
                  <i className="bx bx-plus me-1"></i>
                  Add Pattern
                </>
              )}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default PIIPatternPage;
